import { computed } from "vue";
import { useHead, useSeoMeta } from "@unhead/vue";
import { type RouteLocationNormalizedLoaded, type Router, useRoute, useRouter } from "vue-router";

import * as tmi from "@/constants";
import { notFoundKey } from "@/types/products/product-keys";
import { appUrl } from "@/composables/useShare";
import { titleBadgeCount, titleWithBadge } from "@/composables/useTitleBadge";
import {
    absoluteImageUrl,
    DEFAULT_DESCRIPTION,
    DEFAULT_OG_IMAGE_PATH,
    SITE_NAME,
    siteTitle
} from "@/composables/usePageSeo";

/** The route's own title (`meta.title`), when it has one. */
export function routeTitle( route: Pick< RouteLocationNormalizedLoaded, 'meta' > ): string | undefined {
    const title: unknown = route.meta?.title;
    return typeof title === 'string' && title.trim() ? title.trim() : undefined;
}

/**
 * Whether search engines may index this route (rank 105). Only `meta.indexEligible` routes are; the 404 and 403
 * screens never are, whatever their meta says: both are served as a 200 SPA shell, so without a noindex they were
 * indexable soft errors.
 */
export function isIndexableRoute( route: Pick< RouteLocationNormalizedLoaded, 'meta' | 'name' > ): boolean {
    if ( route.name === notFoundKey || route.name === tmi.ForbiddenName ) return false;
    return true === route.meta?.indexEligible;
}

/**
 * The canonical address of the route on screen: its record's own path (an alias such as /dashboard or
 * /products-advanced/:slug resolves to the primary path), without the query or hash.
 */
export function canonicalPath( router: Router, route: Pick< RouteLocationNormalizedLoaded, 'name' | 'params' | 'path' > ): string {
    if ( route.name == null ) return route.path;
    try {
        return router.resolve( { name: route.name, params: route.params } ).path;
    } catch {
        return route.path;
    }
}

/**
 * The app-wide head (ranks 49 and 105). Call once, in App.vue's setup.
 *
 * - The title template adds the site name and the '(N) ' badge (useTitleBadge) to whichever title wins: a page's
 *   usePageSeo title, else the route's meta.title, else the site name alone.
 * - robots follows `meta.indexEligible` synchronously from the route. index.html never carries a noindex: a crawler
 *   that does not run the app would drop every page.
 * - A canonical link, on indexable routes only.
 * - The link-preview defaults a page without its own usePageSeo falls back to.
 */
export function useAppHead(): void {
    const route = useRoute();
    const router = useRouter();

    const title = computed( () => routeTitle( route ) );
    const indexable = computed( () => isIndexableRoute( route ) );
    const canonicalUrl = computed( () => indexable.value ? appUrl( canonicalPath( router, route ) ) : undefined );
    const defaultImage = absoluteImageUrl( DEFAULT_OG_IMAGE_PATH );

    // A new function whenever the count changes: unhead calls the template outside any reactive effect, so a count
    // read inside it would not be followed. useHead unwraps the ref at runtime; its types only allow a ref of a string.
    const titleTemplate = computed( () => {
        const count = titleBadgeCount.value;
        return ( pageTitle?: string ) => titleWithBadge( siteTitle( pageTitle ), count );
    } ) as unknown as ( pageTitle?: string ) => string;

    useHead( {
        titleTemplate,
        title,
        link: computed( () => canonicalUrl.value ? [ { rel: 'canonical', href: canonicalUrl.value } ] : [] )
    } );

    useSeoMeta( {
        description: DEFAULT_DESCRIPTION,
        robots: computed( () => indexable.value ? 'index, follow' : 'noindex' ),
        ogSiteName: SITE_NAME,
        ogType: 'website',
        ogTitle: computed( () => title.value ?? SITE_NAME ),
        ogDescription: DEFAULT_DESCRIPTION,
        ogUrl: computed( () => canonicalUrl.value ?? appUrl( route.path ) ),
        ogImage: defaultImage,
        twitterCard: 'summary_large_image',
        twitterTitle: computed( () => title.value ?? SITE_NAME ),
        twitterDescription: DEFAULT_DESCRIPTION,
        twitterImage: defaultImage
    } );
}
